import React from 'react';
import { Link } from 'react-router5';
import { useLocale } from 'locale';
import { HistoryItem } from 'store/state/selectors/insights/prices';
import { TableColumn } from 'ds/components/table';
import { Route } from 'config/routes';
import { ColumnRendererProps, PricesTableColumn } from '../../../utils';
import { HistoryAddressLabel, HistoryAddressLabelCell, HistoryPricesColumn } from './styled';
import { ThisUnitLabelHistory } from 'components/widget/prices/prices-table/tables/history/styled';

export const getHistoryAddressColumnConfig = (): Partial<TableColumn<HistoryItem>> => ({
  sortFn: (items: HistoryItem[]) => items.sort((a, b) => a.address && b.address ? b.address.localeCompare(a.address) : -1),
  width: '20%',
  minWidth: '20%',
  gap: 5,
});

type HistoryAddressColumnProps = ColumnRendererProps<HistoryItem> & {
  isCompare: boolean,
  isCurrent: boolean,
  projectId: string,
  projectIds: string[],
  isAddressMaskedNoDistance: boolean,
  nofollow?: string,
};

export const HistoryAddressColumn: React.FC<HistoryAddressColumnProps> = ({
  item,
  isCompare,
  isCurrent,
  projectId,
  projectIds,
  isAddressMaskedNoDistance,
  nofollow,
}) => {
  const { t } = useLocale();

  const hasProjectMultipleIds = item && item.projectIds ? item.projectIds.length > 1 : false;

  let someProjectsHasIds = null;
  if (item.projectIds && projectIds) {
    const projectIdsSet = new Set<string>(projectIds);
    someProjectsHasIds = item.projectIds.some(id => projectIdsSet.has(id));
  }

  const isCurrentProject = item.projectIds && projectId ? item.projectIds.includes(projectId) : someProjectsHasIds;

  const addressLabel = item.projectName ? t('widget.prices.addressLabel', {
    hasProjectMultipleIds,
    isCurrentProject,
    projectName: item.projectName,
  }) : item.address || t('widget.prices.unknownAddress');

  const address = (
    <HistoryAddressLabel title={item.address}>
      <HistoryAddressLabelCell>{addressLabel}</HistoryAddressLabelCell>
    </HistoryAddressLabel>
  );

  const renderContent = () => {
    if (isAddressMaskedNoDistance && isCurrent) {
      return (
        <ThisUnitLabelHistory>
          {item.currentPoiLabel}
        </ThisUnitLabelHistory>
      );
    }

    return (
      <Link
        routeName={isCompare ? Route.UnitPage : Route.Sold}
        routeParams={{ id: item.id }}
        rel={nofollow}
      >
        {address}
      </Link>
    );
  };

  return (
    <HistoryPricesColumn col={PricesTableColumn.Address} isCompare={isCompare} isCurrent={isCurrent}>
      {renderContent()}
    </HistoryPricesColumn>
  );
};
